import { Component } from '@theme/component';

/**
 * Submits the customer signup form in the background so the page doesn't reload, then reveals the
 * success or error message the block already renders.
 *
 * Falls back to a native submit when the module hasn't loaded yet, so the form works without JS.
 *
 * @typedef {object} EmailSignupRefs
 * @property {HTMLInputElement} input - The email input
 * @property {HTMLButtonElement} button - The submit button
 * @property {HTMLElement} inputGroup - The input and button, hidden once posted
 * @property {HTMLElement} successMessage - The success message, hidden until posted
 * @property {HTMLElement} errorMessage - The error message, hidden until a submission fails
 * @property {HTMLElement} errorText - The error message text, pre-filled with a generic fallback
 *
 * @extends {Component<EmailSignupRefs>}
 */
export class EmailSignupComponent extends Component {
  requiredRefs = ['input', 'button', 'inputGroup', 'successMessage', 'errorMessage', 'errorText'];

  #submitting = false;

  get #form() {
    return this.querySelector('form');
  }

  /**
   * @param {MouseEvent} event - The click on the submit button.
   */
  handleSubmit(event) {
    event.preventDefault();
    this.#maybeSubmit();
  }

  /**
   * Handles implicit submission, so pressing Enter behaves like clicking the button.
   *
   * @param {KeyboardEvent} event - The keydown on the input.
   */
  handleKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.#maybeSubmit();
  }

  /*
   * Nothing here lets a `submit` event fire. Shopify's bot protection hooks submits of customer
   * forms posting to /contact and completes them itself, which preventDefault cannot stop — it
   * reloads the page even with reCAPTCHA disabled. So validation happens here instead of relying
   * on the browser's own pre-submit check.
   */
  #maybeSubmit() {
    const form = this.#form;

    if (!form || this.#submitting || !form.reportValidity()) return;

    this.#submit();
  }

  async #submit() {
    const { button, successMessage, errorMessage } = this.refs;
    const form = this.#form;

    if (!form) return;

    this.#submitting = true;
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    successMessage.hidden = true;
    errorMessage.hidden = true;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      /*
       * Shopify screens some background posts (captcha, rate limiting). Resubmit for real so it runs
       * its full-page flow, rather than showing an error we invented. requestSubmit fires the submit
       * event Shopify's protection listens for; form.submit() would skip it.
       */
      if (!response.ok || response.url.includes('/challenge')) {
        form.requestSubmit();
        return;
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const posted = response.url.includes('customer_posted=true') || shown(html, successMessage.id);

      if (posted) {
        this.#showSuccess();
      } else {
        this.#showError(shown(html, errorMessage.id)?.querySelector('[ref="errorText"]')?.textContent);
      }
    } catch (error) {
      console.warn('[email-signup] Submission failed:', error);
      this.#showError();
    } finally {
      this.#submitting = false;
      button.disabled = false;
      form.removeAttribute('aria-busy');
    }
  }

  #showSuccess() {
    const { input, inputGroup, successMessage } = this.refs;

    this.#form?.reset();
    input.removeAttribute('aria-invalid');
    input.setAttribute('aria-describedby', successMessage.id);
    inputGroup.hidden = true;
    successMessage.hidden = false;
  }

  /**
   * @param {string | null} [message] - The message rendered by the server, if there was one.
   */
  #showError(message) {
    const { input, errorMessage, errorText } = this.refs;

    if (message?.trim()) errorText.textContent = message.trim();

    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', errorMessage.id);
    errorMessage.hidden = false;
  }
}

/**
 * Finds a message the server rendered as visible. Both messages are always in the markup, so the
 * `hidden` attribute is what distinguishes a real response from a dormant placeholder.
 *
 * @param {Document} html - The parsed response.
 * @param {string} id - The message element id.
 * @returns {HTMLElement | null} The message element, or null when absent or hidden.
 */
function shown(html, id) {
  const element = html.getElementById(id);

  return element && !element.hasAttribute('hidden') ? element : null;
}

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignupComponent);
}
